/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Config
import physiomKineApi from '@/configs/physiom-kine-api.config';

// Helpers
import { makeUrl } from '@/helpers/api.helper';

// Types
import { Beneficiary, Gender } from '@/types/beneficiary';
import { ProgramAPIResponse } from '@/types/program';

/* -------------------------------------------------------------------------- */
/*                               Service class                                */
/* -------------------------------------------------------------------------- */
type GetBeneficiariesArgs = {
  search?: string;
  status?: string;
  page?: number;
  pageSize?: number;
  withVideo?: boolean;
};

type GetBeneficiariesApiResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Beneficiary[];
};

type InviteBeneficiaryArgs = {
  email: string;
  firstname: string;
  lastname: string;
  company?: string;
  inviter: string;
};

type DeleteBeneficiaryArgs = {
  id: string;
};

type ResendInvitationArgs = {
  id: string;
};

type DeleteInvitationArgs = {
  id: string;
};

type GetOneBeneficiaryArgs = {
  id: string;
};

type UpdateBeneficiaryArgs = {
  params: {
    id: string;
  };
  data: {
    email?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    company?: string | null;
    gender?: string | null;
    groups?: { name: string }[] | null;
    username?: string | null;
    birth_date?: string | null;
    phone?: string | null;
    is_active?: boolean | null;
  };
};

type GetBeneficiaryDetailsArgs = {
  id: string;
};

type GetBeneficiaryDetailsAPIResponse = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  birth_date: string;
  gender: Gender;
  height: number | null;
  weight: number | null;
  medical_history: string | null;
  sleep: string | null;
  social: string | null;
  psychology: string | null;
  functional_assessment: string | null;
  muscular_assessment: string | null;
  joint_assessment: string | null;
  morphological_assessment: string | null;
  prevention_plans: ProgramAPIResponse[];
} | null;

class BeneficiaryService {
  async get(args: GetBeneficiariesArgs): Promise<GetBeneficiariesApiResponse> {
    const apiResponse = (await physiomKineApi.get(
      makeUrl({
        path: '/beneficiary',
        searchParams: {
          search: args.search,
          page: args.page,
          pageSize: args.pageSize,
          status: args.status,
          with_video: args.withVideo,
        },
      })
    )) as GetBeneficiariesApiResponse;

    return apiResponse;
  }

  async invite(args: InviteBeneficiaryArgs) {
    const apiResponse = await physiomKineApi.post(
      makeUrl({ path: `auth/beneficiary/invitation` }),
      {
        email: args.email,
        first_name: args.firstname,
        last_name: args.lastname,
        company: args.company,
        inviter: args.inviter,
      }
    );
    return apiResponse;
  }

  async delete(args: DeleteBeneficiaryArgs) {
    const apiResponse = await physiomKineApi.delete(
      makeUrl({ path: `users/${args.id}` })
    );
    return apiResponse;
  }

  async resendInvitation(args: ResendInvitationArgs) {
    const apiResponse = await physiomKineApi.post(
      makeUrl({ path: `/auth/beneficiary/invitation/${args.id}/resend` })
    );
    return apiResponse;
  }

  async deleteInvitation(args: DeleteInvitationArgs) {
    const apiResponse = await physiomKineApi.delete(
      makeUrl({ path: `/invitations/${args.id}/` })
    );
    return apiResponse;
  }

  async getOne(args: GetOneBeneficiaryArgs) {
    const apiResponse = (await physiomKineApi.get(
      makeUrl({ path: `/users/${args.id}/` })
    )) as Beneficiary;

    return apiResponse;
  }

  async update(args: UpdateBeneficiaryArgs) {
    const apiResponse = await physiomKineApi.patch(
      makeUrl({ path: `/users/${args.params.id}/` }),
      { ...args.data }
    );

    return apiResponse;
  }

  async getBeneficiaryDatails(args: GetBeneficiaryDetailsArgs) {
    const apiResponse = (await physiomKineApi.get(
      makeUrl({ path: `/beneficiary/${args.id}/detail` })
    )) as GetBeneficiaryDetailsAPIResponse;

    return apiResponse;
  }
}

export {
  BeneficiaryService,
  type GetBeneficiariesArgs,
  type InviteBeneficiaryArgs,
  type ResendInvitationArgs,
  type UpdateBeneficiaryArgs,
  type GetBeneficiaryDetailsAPIResponse,
};
